import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, LayoutDashboard, PenLine, ShieldCheck } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <>
      <style>{`
        /* Phase 1-2: Logo gallops in from left, then bounces to settle */
        @keyframes logoGallop {
          0%   { transform: translateX(-120%); opacity: 0; }
          55%  { transform: translateX(2%);   opacity: 1; }
          70%  { transform: translateX(-1%) scale(1.05); }
          85%  { transform: translateX(0)    scale(0.98); }
          100% { transform: translateX(0)    scale(1); }
        }

        /* Phase 3: Continuous breathing pulse */
        @keyframes logoPulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.03); }
        }

        /* Phase 4: Tagline fades up */
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Phase 5: Cards slide up */
        @keyframes cardRise {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .logo-wrap {
          animation: logoGallop 0.9s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .logo-pulse {
          animation: logoPulse 3s ease-in-out infinite;
          animation-delay: 1.2s;
        }
        .tagline {
          opacity: 0;
          animation: fadeUp 0.5s ease-out forwards;
          animation-delay: 0.85s;
        }
        .card-left {
          opacity: 0;
          animation: cardRise 0.55s ease-out forwards;
          animation-delay: 1.1s;
        }
        .card-right {
          opacity: 0;
          animation: cardRise 0.55s ease-out forwards;
          animation-delay: 1.25s;
        }
        .card-third {
          opacity: 0;
          animation: cardRise 0.55s ease-out forwards;
          animation-delay: 1.4s;
        }
        .card-fourth {
          opacity: 0;
          animation: cardRise 0.55s ease-out forwards;
          animation-delay: 1.55s;
        }

        .portal-card {
          background: #0F2236;
          border: 1px solid #1E3A5F;
          border-radius: 16px;
          padding: 40px 36px;
          cursor: pointer;
          transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
          flex: 1;
          min-width: 260px;
          max-width: 340px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 12px;
        }
        .portal-card:hover {
          border-color: #2563EB;
          box-shadow: 0 0 28px rgba(37, 99, 235, 0.25), 0 4px 24px rgba(0,0,0,0.4);
          transform: translateY(-3px);
        }
        .portal-card:active {
          transform: translateY(-1px);
        }
        .card-icon {
          width: 48px;
          height: 48px;
          background: rgba(37, 99, 235, 0.12);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 4px;
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: '#0D1B2A',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        overflow: 'hidden',
        position: 'relative',
      }}>

        {/* בס״ד — top right */}
        <span style={{
          position: 'absolute',
          top: '18px',
          right: '22px',
          color: '#7A9BBF',
          fontSize: '13px',
          letterSpacing: '0.06em',
          fontWeight: 500,
          opacity: 0.75,
          userSelect: 'none',
        }}>בס״ד</span>

        {/* Logo */}
        <div className="logo-wrap" style={{ textAlign: 'center' }}>
          <div className="logo-pulse" style={{ display: 'inline-block' }}>
            <img
              src="/logo.png"
              alt="Power Funding Solutions"
              style={{
                maxWidth: '220px',
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          </div>
        </div>

        {/* Tagline */}
        <p className="tagline" style={{
          color: '#7A9BBF',
          fontSize: '13px',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          marginTop: '14px',
          marginBottom: '56px',
          fontWeight: 400,
        }}>
          POWERED BY INNOVATION ... AND JAY
        </p>

        {/* Cards */}
        <div style={{
          display: 'flex',
          gap: '24px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '1480px',
        }}>

          {/* Card 1 — AI */}
          <div
            className="portal-card card-left"
            onClick={() => { window.location.href = 'https://ai.powerclaw.win'; }}
            data-testid="landing-ai-card"
          >
            <div className="card-icon">
              <Brain size={24} color="#2563EB" strokeWidth={1.75} />
            </div>
            <div>
              <h2 style={{
                color: '#FFFFFF',
                fontSize: '20px',
                fontWeight: 600,
                margin: '0 0 6px',
                letterSpacing: '-0.01em',
              }}>
                AI Assistant
              </h2>
              <p style={{
                color: '#7A9BBF',
                fontSize: '14px',
                margin: 0,
                lineHeight: 1.5,
              }}>
                Intelligent underwriting &amp; decision support
              </p>
            </div>
          </div>

          {/* Card 2 — Portal */}
          <div
            className="portal-card card-right"
            onClick={() => navigate('/login')}
            data-testid="landing-portal-card"
          >
            <div className="card-icon">
              <LayoutDashboard size={24} color="#2563EB" strokeWidth={1.75} />
            </div>
            <div>
              <h2 style={{
                color: '#FFFFFF',
                fontSize: '20px',
                fontWeight: 600,
                margin: '0 0 6px',
                letterSpacing: '-0.01em',
              }}>
                PFS Portal
              </h2>
              <p style={{
                color: '#7A9BBF',
                fontSize: '14px',
                margin: 0,
                lineHeight: 1.5,
              }}>
                Deal management, syndication &amp; reporting
              </p>
            </div>
          </div>

          {/* Card 3 — Signport (10/09, Josh) */}
          <div
            className="portal-card card-third"
            onClick={() => navigate('/signport')}  /* 10/09: goes through the PFS login first (lib/signport.js) */
            data-testid="landing-signport-card"
          >
            <div className="card-icon">
              <PenLine size={24} color="#2563EB" strokeWidth={1.75} />
            </div>
            <div>
              <h2 style={{
                color: '#FFFFFF',
                fontSize: '20px',
                fontWeight: 600,
                margin: '0 0 6px',
                letterSpacing: '-0.01em',
              }}>
                Signport
              </h2>
              <p style={{
                color: '#7A9BBF',
                fontSize: '14px',
                margin: 0,
                lineHeight: 1.5,
              }}>
                Contracts &amp; e-signatures (login required)
              </p>
            </div>
          </div>

          {/* Card 4 — Funding Confirmation (10/23, Josh) */}
          <div
            className="portal-card card-fourth"
            onClick={() => navigate('/funding-confirmations')}  /* ProtectedRoute → login first, then back here */
            data-testid="landing-funding-confirmation-card"
          >
            <div className="card-icon">
              <ShieldCheck size={24} color="#2563EB" strokeWidth={1.75} />
            </div>
            <div>
              <h2 style={{
                color: '#FFFFFF',
                fontSize: '20px',
                fontWeight: 600,
                margin: '0 0 6px',
                letterSpacing: '-0.01em',
              }}>
                Funding Confirmation
              </h2>
              <p style={{
                color: '#7A9BBF',
                fontSize: '14px',
                margin: 0,
                lineHeight: 1.5,
              }}>
                Merchant pre-funding acknowledgments (login required)
              </p>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Landing;
